import { useMemo, useState } from 'react'
import { useBlacklistSummary, useConfig } from '@/api/queries'
import { Badge } from '@/components/ui/Badge'
import { Button, LinkButton } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { Icons } from '@/components/ui/Icons'
import { Widget } from '@/components/ui/Widget'
import { THRESHOLDS } from '@/features/attention/attention'
import { BlacklistDialog } from './BlacklistDialog'
import { groupByPos, type PosScope } from './blacklistByPos'
import styles from './BlacklistWidget.module.css'

/**
 * Blacklisted IPs per POS: one spider per POS, hanging at the end of a thread as long as its count, with the
 * number written out. A row opens the IP list filtered on that POS; the header opens the whole list.
 */
export function BlacklistWidget({ className }: { className?: string }) {
  const { data, isLoading, error } = useBlacklistSummary()
  const { data: config } = useConfig()
  const [list, setList] = useState<{ scope?: PosScope } | null>(null)

  const total = data?.reduce((sum, d) => sum + d.blacklistedIpCount, 0) ?? 0
  const rows = useMemo(() => groupByPos(data ?? []), [data])
  const longest = Math.max(1, ...rows.map((r) => r.count))

  return (
    <Widget
      title={
        <>
          <Icons.Spider size={16} />
          Blacklisted IPs
        </>
      }
      onTitleClick={() => setList({})}
      loading={isLoading}
      className={className}
      actions={
        <>
          {data && <Badge tone={total > 0 ? 'danger' : 'ok'}>{total} blacklisted</Badge>}
          <Button size="sm" icon={<Icons.Menu size={12} />} onClick={() => setList({})} title="List every blacklisted IP">
            View IPs
          </Button>
          {config?.links.grafanaProxyPipeline && (
            <LinkButton size="sm" href={config.links.grafanaProxyPipeline} icon={<Icons.External size={12} />} title="Proxy pipeline board in Grafana">
              Grafana
            </LinkButton>
          )}
        </>
      }
    >
      {error ? (
        <ErrorState error={error} />
      ) : data && rows.length === 0 ? (
        <EmptyState>No blacklisted IP</EmptyState>
      ) : (
        <>
          <ul className={styles.rows}>
            {rows.map((row) => {
              const heavy = row.count > THRESHOLDS.blacklistedIpsPerSite
              return (
                <li key={row.key}>
                  <button
                    type="button"
                    className={`${styles.row} ${heavy ? styles.heavy : ''}`}
                    onClick={() => setList({ scope: { label: row.label, pos: row.pos } })}
                    title={`Show the ${row.count} blacklisted IPs of ${row.label}`}
                  >
                    <span className={styles.name}>{row.label}</span>
                    <span className={styles.track}>
                      <span className={styles.thread} style={{ width: `${Math.max(3, (row.count / longest) * 100)}%` }}>
                        <Icons.Spider size={20} className={styles.spider} />
                      </span>
                    </span>
                    <span className={styles.count}>{row.count}</span>
                  </button>
                </li>
              )
            })}
          </ul>
          <p className={styles.legend}>Thread length is the number of blacklisted IPs. Red above {THRESHOLDS.blacklistedIpsPerSite}.</p>
        </>
      )}
      {list && <BlacklistDialog scope={list.scope} onClose={() => setList(null)} />}
    </Widget>
  )
}
